import { useState, useEffect } from 'react';
import { patientJourney } from '../mockData';
import { CheckCircle2, ChevronRight, Stethoscope, Plane, Building2, Ticket, HeartPulse, UserCheck } from 'lucide-react';

const iconMap = {
  1: Stethoscope, // Consultation
  2: Ticket,      // Visa/Travel
  3: Plane,       // Arrival
  4: Building2,   // Hospitalization
  5: HeartPulse,  // Treatment
  6: UserCheck    // Recovery
};

export const PatientJourney = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('journey');
    if (element) observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, []);

  return (
    <section id="journey" className="py-24 bg-gradient-to-b from-white to-gray-50 overflow-hidden relative">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-0 top-1/4 w-64 h-64 bg-brand-blue/5 rounded-full blur-3xl opacity-50 animate-pulse"></div>
        <div className="absolute right-0 bottom-1/4 w-96 h-96 bg-brand-teal/5 rounded-full blur-3xl opacity-50 delay-1000 animate-pulse"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div
          className={`text-center mb-20 transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
        >
          <div className="inline-block px-5 py-2 bg-brand-teal/10 text-brand-teal rounded-full text-sm font-bold mb-6 tracking-wide uppercase hover:bg-brand-teal hover:text-white transition-colors duration-300">
            Your Path to Recovery
          </div>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-gray-900 mb-6 leading-tight">
            Patient Journey <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-brand-teal">Timeline</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            A seamless, step-by-step process designed for your comfort, from initial consultation to complete recovery.
          </p>
        </div>

        <div className="max-w-6xl mx-auto relative">
          {/* Central Connecting Line (Desktop) */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-brand-blue/20 via-brand-teal/20 to-brand-blue/20 transform -translate-x-1/2 rounded-full"></div>

          {/* Animated Progress Line (Scroll based - simplified as full animation for now) */}
          <div className={`hidden lg:block absolute left-1/2 top-0 w-1 bg-gradient-to-b from-brand-blue via-brand-teal to-brand-blue transform -translate-x-1/2 rounded-full origin-top transition-all duration-[2s] ease-out ${isVisible ? 'h-full' : 'h-0'}`}></div>

          <div className="space-y-12 lg:space-y-0">
            {patientJourney.map((step, index) => {
              const Icon = iconMap[step.id] || CheckCircle2;
              const isEven = index % 2 === 0;

              return (
                <div
                  key={step.id}
                  className={`relative flex flex-col lg:flex-row items-center justify-center lg:justify-between w-full group transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
                    }`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                  onMouseEnter={() => setActiveStep(step.id)}
                  onMouseLeave={() => setActiveStep(null)}
                >
                  {/* Left Side (Text or Spacer) */}
                  <div className={`w-full lg:w-[45%] mb-8 lg:mb-0 ${isEven ? 'order-1 lg:text-right lg:pr-12' : 'order-1 lg:order-3 lg:text-left lg:pl-12'}`}>
                    <div
                      className={`bg-white rounded-3xl p-8 shadow-sm border border-gray-100 relative transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:border-brand-blue/20 group-hover:scale-[1.02] ${activeStep === step.id ? 'ring-2 ring-brand-blue/10 bg-blue-50/10' : ''
                        }`}
                    >
                      {/* Decorative Quote mark or shape */}
                      <div className={`absolute top-0 ${isEven ? 'right-0 rounded-bl-3xl' : 'left-0 rounded-br-3xl'} w-16 h-16 bg-gradient-to-br from-brand-blue/5 to-brand-teal/5`}></div>

                      <div className={`flex items-center gap-4 mb-4 ${isEven ? 'lg:flex-row-reverse' : ''}`}>
                        <span className="text-4xl font-black text-gray-100 group-hover:text-brand-blue/10 transition-colors duration-300">0{index + 1}</span>
                        <h3 className="text-2xl font-bold text-gray-900 group-hover:text-brand-blue transition-colors duration-300">
                          {step.title}
                        </h3>
                      </div>

                      <p className="text-gray-600 leading-relaxed font-medium">
                        {step.description}
                      </p>

                      {/* Arrow indicator on hover */}
                      <div className={`mt-4 flex items-center text-brand-teal font-semibold text-sm opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-0 group-hover:translate-x-2 ${isEven ? 'lg:justify-end lg:group-hover:-translate-x-2 lg:group-hover:translate-x-0' : ''}`}>
                        Next Step <ChevronRight className="w-4 h-4 ml-1" />
                      </div>
                    </div>
                  </div>

                  {/* Center Timeline Node */}
                  <div className="order-2 lg:absolute lg:left-1/2 lg:transform lg:-translate-x-1/2 z-20">
                    <div className="relative">
                      {/* Pulse effect rings */}
                      <div className="absolute inset-0 bg-brand-blue/20 rounded-full animate-ping opacity-0 group-hover:opacity-100 group-hover:animation-play-state-running"></div>

                      {/* Main Cirle */}
                      <div className={`w-16 h-16 lg:w-20 lg:h-20 rounded-full flex items-center justify-center border-4 border-white shadow-xl transition-all duration-500 group-hover:scale-110 ${activeStep === step.id ? 'bg-gradient-to-br from-brand-blue to-brand-teal' : 'bg-white'
                        }`}>
                        <Icon className={`w-8 h-8 lg:w-10 lg:h-10 transition-colors duration-300 ${activeStep === step.id ? 'text-white' : 'text-brand-blue'
                          }`} />
                      </div>

                      {/* Line Connector for specific items if needed */}
                      {index !== patientJourney.length - 1 && (
                        <div className="lg:hidden absolute top-full left-1/2 w-0.5 h-12 bg-gray-200 -ml-[1px]"></div>
                      )}
                    </div>
                  </div>

                  {/* Right Side (Spacer or Text) */}
                  <div className={`hidden lg:block w-[45%] ${isEven ? 'order-3' : 'order-1'} `}>
                    {/* Empty spacer to balance flex layout */}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};